// @ts-strict-ignore
import AppointmentUSPBlock from '@components/AppointmentUSPBlock'
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import { TudorContactForm } from '@components/TudorContactForm'
import ProductDetailSection from '@components/_product-detail/ProductDetailSection/ProductDetailSection'
import ProductTabs from '@components/_product-detail/ProductTabs'
import RelatedProducts from '@components/_product-detail/RelatedProducts'
import SaveRecentlyViewed from '@components/_product-detail/SaveRecentlyViewed'
import { getArticleCategoryHref } from '@components/_product-detail/ProductDetailSection/helpers/get-article-category-href'
import { jewelTypeMap } from '@components/_product-detail/jewelTypeMap'
import { SchemaOrg } from '@components/schema-org'
import { Article, ArticleDocument } from '@generated'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { articlesToDataLayerItems } from '@lib/data-layer-social-ads/helpers/articlesToDataLayerItems'
import { dataLayerViewItem } from '@lib/data-layer-social-ads/view-item'
import { emarsysProductVisit } from '@lib/emarsys-tracking/emarsys-product-visit'
import { getPreOwnedProductDescription } from '@lib/get-pre-owned-product-description'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { getUrlSlugInLocale } from '@lib/get-url-slug-in-locale'
import { initializeContactForm } from '@lib/initialize-contact-form'
import { useAuth } from '@lib/hooks/use-auth'
import { useCart } from '@lib/hooks/useCart'
import { getProductDescription } from '@lib/kontent'
import { mustacheRenderSafely } from '@lib/mustache-render-safely'
import pinterestTracking from '@lib/pinterest-tracking'
import { PageProps } from '@lib/types'
import { createUrqlClient } from '@lib/urqlClient'
import { useEmarsysStore } from '@lib/use-emarsys-store'
import ErrorPage from '@pages/_error'
import mustache from 'mustache'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import slugify from 'slugify'

type Data = {
  article: Article
  title: string | null
  brandDescription: string | null
  productDescription: string | null
  metaTitle: string | null
  metaDescription: string | null
  productSpecificUsps: string[] | null
}

let timer

const ProductDetailPage: NextPage<PageProps<Data>> = ({ statusCode, data }) => {
  const [ref, hideMainCtaMobile] = useInView()
  const {
    t,
    i18n: { language },
  } = useTranslation('other')
  const { scriptIsLoaded } = useEmarsysStore()
  const cart = useCart()
  const { me } = useAuth()
  const customerId = me && me.dafUser && me.dafUser.klant_nr
  const router = useRouter()
  const isPopover = typeof router.query.popover !== 'undefined'
  const canonical =
    language === 'nl' ? `/p/${data.article.urlSlug}` : `/${language}/p/${data.article.urlSlug}`

  useEffect(() => {
    if (router.query.appointment) {
      timer = setTimeout(() => {
        initializeContactForm('appointment')
      }, 500)
    }

    return () => {
      clearTimeout(timer)
    }
  }, []) //eslint-disable-line

  useEffect(() => {
    genericPushToTrackingLayer('Ecommerce', 'productImpressions')

    if (data.article && cart && scriptIsLoaded) {
      emarsysProductVisit({
        productId: data.article.id,
        customerId,
        cart,
      })
    }
    pinterestTracking.pageVisit()

    const dataLayerItem = articlesToDataLayerItems([data.article] as any)
    dataLayerViewItem(dataLayerItem[0])
  }, [data, scriptIsLoaded, cart]) // eslint-disable-line

  if (statusCode) {
    return <ErrorPage statusCode={statusCode} />
  }
  if (!data.article) {
    return <ErrorPage statusCode={404} />
  }

  const hasCustomContactForm = data.article.brand.toLowerCase() === 'tudor'
  const categoryHref = getArticleCategoryHref(
    data.article.brand,
    data.article.articleType,
    language,
  )

  let breadcrumbs = [
    {
      href: categoryHref,
      label: data.article.brand,
    },
    {
      href: `/p/${data.article.urlSlug}`,
      label: data.article.description,
    },
  ]
  const jewelTypeId = data.article.articleAttributes.find(
    (attribute) => attribute.attributeName === 'Type Jewel',
  )
  if (jewelTypeId) {
    const jewelTypeMapKey = jewelTypeMap[jewelTypeId.attributeValueID]

    // Add a breadcrumb if jewelTypeMapKey exists and we have a matching Kentico page in messages.urlSlugItems
    if (jewelTypeMapKey && getUrlSlugInLocale(jewelTypeMapKey.urlSlugKey, language)) {
      breadcrumbs.unshift({
        href: `/${getUrlSlugInLocale(jewelTypeMapKey.urlSlugKey, language)}`,
        label: jewelTypeMapKey.title[language],
      })
    }
  }

  if (data.article.articleType === 'Horloge') {
    breadcrumbs.unshift({
      href: `/${getUrlSlugInLocale('watches', language)}`,
      label: t('watches'),
    })
  }

  const title = data.title || data.article.brand

  return (
    <Page
      title={`${title} - GASSAN`}
      description={data.metaDescription}
      additionalMetaTags={[{ property: 'title', content: data.title }]}
      canonical={canonical}
      languageAlternates={[
        { href: `/nl/p/${data.article.urlSlug}`, hrefLang: 'nl' },
        { href: `/en/p/${data.article.urlSlug}`, hrefLang: 'en' },
        { href: `/de/p/${data.article.urlSlug}`, hrefLang: 'de' },
      ]}
    >
      <SchemaOrg variant="gassan-product" data={data.article} />
      <FloatingServiceMenu
        variant={hideMainCtaMobile ? 'default' : 'move-element-up-product-page'}
      />
      <SaveRecentlyViewed id={data.article.id} />
      <Header />
      <PageTransition>
        <ProductDetailSection
          article={data.article}
          productSpecificUsps={data.productSpecificUsps}
          onOpenContactModal={initializeContactForm}
          hideMainCtaMobile={hideMainCtaMobile}
          language={language}
          galleryVariant={isPopover ? 'popover' : 'default'}
        />
        <ProductTabs
          article={data.article}
          brandDescription={data.brandDescription}
          productDescription={data.productDescription}
        />
        {hasCustomContactForm && (
          <TudorContactForm
            subject={`Vraag over ${data.article.brand} - ${data.article.id}`}
            productId={data.article.id}
          />
        )}
        <RelatedProducts article={data.article} />
        <div ref={ref}>
          {!hasCustomContactForm && (
            <AppointmentUSPBlock
              mt="5rem"
              subject={`Vraag over ${data.article.brand} - ${data.article.id}`}
              variant="product-page"
              productId={data.article.id}
              formType={data.article.webID === 'gpcpreown' ? 'pre-owned' : 'default'}
            />
          )}
          <Footer />
        </div>
      </PageTransition>
    </Page>
  )
}

const getAttributesMap = (data: Article) => {
  const { articleAttributes, __typename, images, ...rest } = data
  let attributes = {}
  Object.keys(rest).forEach((key) => {
    if (typeof rest[key] === 'string') {
      attributes[key] = rest[key]
    }
    if (typeof rest[key] === 'number') {
      attributes[key] = rest[key]
    }
  })
  articleAttributes.forEach((attr) => {
    attributes[attr.attributeOriginalName] = attr.attributeValue
  })
  return attributes
}

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const id = (params.slug as string).split('-').pop()
  const client = createUrqlClient(locale, true)
  try {
    const { data } = await client.query(ArticleDocument, { id }).toPromise()
    if (!data.article || !data.article.id) {
      return { notFound: true }
    }
    const isPreOwned = data.article.webID === 'gpcpreown'
    const customProductMetaData = await getProductDescription({
      language: locale,
      dafNr: id,
    })

    // Chopard is split into chopard-jewelry & chopard-watches
    let brandSlug = slugify(data.article.brand, { lower: true })

    if (brandSlug === 'chopard') {
      if (data.article.articleType === 'Horloge') {
        brandSlug = locale === 'en' ? 'chopard-watches' : 'chopard-horloges'
      } else {
        brandSlug = locale === 'en' ? 'chopard-jewelry' : 'chopard-juwelen'
      }
    }

    if (isPreOwned) {
      brandSlug = locale === 'en' ? 'pre-owned-watches' : 'pre-owned-horloges'
    }

    // Get metadata
    let title: string
    let brandDescription: string
    let productDescription: string
    let metaTitle: string
    let metaDescription: string
    let productSpecificUsps: string[] | null = null

    // Get file with dynamic content
    let json = {}
    try {
      json = await import(
        `@components/_product-detail/pdp-content/${brandSlug}-${locale}.json`
      ).then((mod) => mod.default)
    } catch (error) {
      console.error(
        `Error loading JSON file for ${brandSlug}-${locale}, maybe it doesn't exist:`,
        error,
      )
    }

    const attributes = getAttributesMap(data.article)
    // https://stackoverflow.com/questions/22910428/mustache-globally-disable-html-escaping/23057003#23057003
    mustache.escape = (value) => value

    title = json['product_detail_title']
      ? mustacheRenderSafely(json['product_detail_title'], brandSlug, attributes)
      : null
    brandDescription = json['product_detail_description']
      ? mustacheRenderSafely(json['product_detail_description'], brandSlug, attributes)
      : null
    productDescription = null
    metaTitle = json['product_detail_meta_title']
      ? mustacheRenderSafely(json['product_detail_meta_title'], brandSlug, attributes)
      : null
    metaDescription = json['product_detail_meta_description']
      ? mustacheRenderSafely(json['product_detail_meta_description'], brandSlug, attributes)
      : null

    // If this is a preowned product, we create a custom product description based on the product data
    if (isPreOwned) {
      productDescription = getPreOwnedProductDescription(locale, data.article)
    }

    // Overwrite all of the above if there is custom content for this product in Kentico
    if (customProductMetaData.item) {
      if (customProductMetaData.item.elements.title.value) {
        title = customProductMetaData.item.elements.title.value
      }
      if (customProductMetaData.item.elements.description.value) {
        productDescription = customProductMetaData.item.elements.description.value
      }
      if (customProductMetaData.item.elements.meta_title.value) {
        metaTitle = customProductMetaData.item.elements.meta_title.value
      }
      if (customProductMetaData.item.elements.meta_description.value) {
        metaDescription = customProductMetaData.item.elements.meta_description.value
      }
      if (customProductMetaData.item.elements.usp_items.value) {
        productSpecificUsps = customProductMetaData.item.elements.usp_items.modular_content.map(
          (key) => customProductMetaData.modular_content[key].elements.text.value,
        ) as string[]
      }
    }
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        data: {
          article: data.article,
          title,
          brandDescription,
          productDescription,
          metaTitle,
          metaDescription,
          productSpecificUsps,
        },
      },
      revalidate: 60 * 30, // Revalidate once every 10 minutes
    }
  } catch (error) {
    console.error(`Error in getStaticProps for slug ${params.slug}:`, error)
    return { notFound: true }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: 'blocking',
    paths: [],
  }
}

export default ProductDetailPage
